import {
  colors,
  customEvent,
  HStack,
  useAlertDialog,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { useOverlay } from "@toss/use-overlay";

import { FixedCustomButton } from "@/components/FixedCustomButton";
import { OverlayModal } from "goi_common/src/components/OverlayModal";
import { BugoShareChoiceModalContent } from "@/components/Modals/BugoShareChoiceModalContent";
import { BankAccountModalContent } from "@/containers/BugoContainer/BugoCreateContainer/components/BugoIntroSection";
import { ObituaryVisitorVersionResponse } from "@/apis/obituaries";
import {
  FLOWER_ORDER_DISABLED_MESSAGE,
  FlowerOrderDisabledReason,
  isTargetObituaryForVisitorFixedFooterVariant,
} from "@/containers/BugoContainer/utils";

type ObituaryForFooter = {
  name?: string;
  age?: number;
  passed_away_on?: string | null;
  bank_accounts?: ObituaryVisitorVersionResponse["bank_accounts"] | null;
};

export const BugoShareVisitorFixedButtonFooter = ({
  flowerOrderDisabledReason,
  obituary,
}: {
  flowerOrderDisabledReason: FlowerOrderDisabledReason;
  obituary?: ObituaryForFooter;
}) => {
  const isEnableFlowerOrder = flowerOrderDisabledReason === null;
  // 특정 부고(박순자님 등)에는 하단 Fixed에 부의금 버튼을 추가한 3-버튼 배치.
  // 그 외 부고는 기존 2-버튼(부고전달/근조화환) 그대로.
  const isTargetVariant =
    isTargetObituaryForVisitorFixedFooterVariant(obituary);
  const bankAccounts = obituary?.bank_accounts;
  /** share 인 경우에는 버튼 안보여줘야함  */
  const router = useRouter();
  const { obituaryKey, sangjuKey } = router.query as {
    obituaryKey?: string;
    sangjuKey?: string;
  };

  const overlay = useOverlay();

  const onClickBankAccount = () => {
    if (!bankAccounts?.length) {
      openAlertDialog({
        content: "등록된 부의금 계좌가 없습니다.",
        buttonText: "확인",
      });
      return;
    }

    overlay.open(({ isOpen, close: onClose }) => (
      <OverlayModal
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={onClose}
        modalContentStyle={{
          maxWidth: "300px",
          borderRadius: "20px",
        }}
        overlayModalContent={
          <BankAccountModalContent bankAccounts={bankAccounts} />
        }
      />
    ));
  };

  const onClickShareVisitorVersion = () => {
    overlay.open(({ isOpen, close: onCloseOverlay }) => {
      const onClose = () => {
        onCloseOverlay();
      };

      return (
        <OverlayModal
          closeOnOverlayClick
          isOpen={isOpen}
          onClose={onClose}
          modalContentStyle={{
            maxWidth: "300px",
            borderRadius: "20px",
          }}
          overlayModalContent={
            <BugoShareChoiceModalContent
              onClose={onClose}
              type="visitor"
              obituaryKey={obituaryKey}
            />
          }
        />
      );
    });
  };

  const { openAlertDialog } = useAlertDialog();

  const onClickFlowerOrder = () => {
    if (isEnableFlowerOrder) {
      router.push(`/flower/select?sangjuKey=${sangjuKey}`);
      return;
    }
    openAlertDialog({
      content: FLOWER_ORDER_DISABLED_MESSAGE[flowerOrderDisabledReason!],
      buttonText: "확인",
    });
  };

  const shareButton = (
    <button
      className="subtitle_16_sb"
      onClick={() => {
        customEvent({
          path: "visitor",
          trigger_and_target: "btn_share-visitor",
        });
        onClickShareVisitorVersion();
      }}
      css={{
        width: "100%",
        padding: "12px",
        borderRadius: "5px",
        background: colors.warmGray850,
        color: colors.gray50,
        boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      부고전달
    </button>
  );

  // target variant(3-버튼) 전용: 부의금과 동일한 심플 톤 (회색 border + 검정 텍스트)
  const flowerButtonSimple = (
    <button
      className="subtitle_16_sb"
      onClick={() => {
        customEvent({
          path: "visitor",
          trigger_and_target: "flower_order_box_button",
        });
        onClickFlowerOrder();
      }}
      css={{
        width: "100%",
        padding: "12px",
        borderRadius: "5px",
        border: `1px solid ${colors.gray300}`,
        background: colors.white,
        color: isEnableFlowerOrder ? colors.warmGray850 : colors.gray500,
        boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      근조화환
    </button>
  );

  // 기본(2-버튼) 전용: 기존 빨강 border + 그라데이션 텍스트 유지
  const flowerButtonAccent = (
    <button
      className="subtitle_16_sb gray50"
      onClick={() => {
        customEvent({
          path: "visitor",
          trigger_and_target: "flower_order_box_button",
        });
        onClickFlowerOrder();
      }}
      css={{
        width: "100%",
        padding: "12px 16px",
        border: isEnableFlowerOrder
          ? "1px solid #FF5A5A"
          : `1px solid ${colors.gray300}`,
        borderRadius: "5px",
        backgroundColor: colors.white,
        boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      {isEnableFlowerOrder ? (
        <span
          css={{
            background: "linear-gradient(90deg, #FF5A5A 0%, #E8BA48 84%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          근조화환
        </span>
      ) : (
        <span css={{ color: colors.gray500 }}>근조화환</span>
      )}
    </button>
  );

  const bankAccountButton = (
    <button
      className="subtitle_16_sb"
      onClick={() => {
        customEvent({
          path: "visitor",
          trigger_and_target: "bank_account_button",
        });
        onClickBankAccount();
      }}
      css={{
        width: "100%",
        padding: "12px",
        borderRadius: "5px",
        border: `1px solid ${colors.gray300}`,
        background: colors.white,
        color: colors.warmGray850,
        boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      부의금
    </button>
  );

  return (
    <FixedCustomButton>
      <VStack gap="16px" width="100%" alignItems="center">
        {isTargetVariant ? (
          /* 3-버튼: 부의금 / 부고전달 / 근조화환 (특정 부고 대상, 심플 톤) */
          <HStack gap="9px" width="100%">
            {bankAccountButton}
            {shareButton}
            {flowerButtonSimple}
          </HStack>
        ) : (
          /* 기존 2-버튼: 부고전달 / 근조화환 (기본, 그라데이션 강조) */
          <HStack gap="9px" width="100%">
            {shareButton}
            {flowerButtonAccent}
          </HStack>
        )}
      </VStack>
    </FixedCustomButton>
  );
};
